import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { STALE, GUEST_VAT_RATE } from '@/lib/constants'
import type { ResolvedSearchParams } from '@/lib/schemas/search'

/**
 * Live guest VAT rate from platform_settings (publicly readable via 0020).
 * Falls back to the GUEST_VAT_RATE constant until loaded / if missing, so the
 * displayed VAT always matches what create_booking_hold charges.
 */
export function useVatRate(): number {
  const { data } = useQuery({
    queryKey: ['vat_rate'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('key', 'vat_rate')
        .maybeSingle()
      if (error) throw error
      const v = Number(data?.value)
      return Number.isFinite(v) ? v : GUEST_VAT_RATE
    },
    staleTime: 5 * 60 * 1000,
  })
  return data ?? GUEST_VAT_RATE
}

// ── Shared types ──────────────────────────────────────────────────────────────

export interface PropertySearchResult {
  id: string
  slug: string
  name: string
  property_type: string
  city: string
  province: string
  star_rating: number | null
  review_score: number | null
  review_count: number
  lowest_rate: number | null          // centavos, null when no availability data
  thumbnail_url: string | null
  lat: number | null
  lng: number | null
  discount_pct: number                 // best active discount % for the search (0 = none)
  rooms_left: number | null            // units left for the cheapest combo; null when no dates
}

export interface RatePlan {
  id: string
  name: string
  cancel_policy: 'free_cancellation' | 'non_refundable'
  free_cancel_deadline_days: number | null
  cancel_penalty: 'first_night' | 'full_amount' | null
  meals_included: string
  price_modifier_pct: number | null
  discount_pct: number
  discount_starts_on: string | null
  discount_ends_on: string | null
}

export interface RoomType {
  id: string
  name: string
  description: string | null
  max_guests: number
  base_occupancy: number
  extra_guest_fee: number
  free_child_min_age: number | null
  free_child_max_age: number | null
  paid_child_min_age: number | null
  paid_child_max_age: number | null
  child_fee: number
  quantity: number
  base_rate: number
  size_sqm: number | null
  bed_configuration: Array<{ type: string; count: number }> | null
  rate_plans: RatePlan[]
  room_amenities?: Array<{ amenity: string }>
  images?: Array<{ id: string; cloudinary_public_id: string; display_order: number }>
}

export interface PropertyImage {
  id: string
  cloudinary_public_id: string
  display_order: number
}

export interface PropertyDetail {
  id: string
  slug: string
  name: string
  property_type: string
  status: string
  description: string | null
  address_line1: string
  city: string
  province: string
  zip: string
  country: string
  lat: number | null
  lng: number | null
  star_rating: number | null
  check_in_time: string
  check_out_time: string
  vat_registered: boolean
  promo_discount_pct: number
  promo_starts_on: string | null
  promo_ends_on: string | null
  review_score: number | null
  review_count: number
  property_images: PropertyImage[]
  property_amenities: Array<{ amenity: string }>
  room_types: RoomType[]
}

export interface PropertyReview {
  id: string
  guest_id: string
  score_overall: number
  score_cleanliness: number
  score_location: number
  score_staff: number
  score_value: number
  score_comfort: number
  score_facilities: number
  comment: string | null
  created_at: string
  profiles: { full_name: string; avatar_url: string | null } | null
}

export interface RoomAvailabilityRow {
  room_type_id: string
  room_type_name: string
  base_quantity: number
  base_rate: number
  date: string
  available_count: number
  rate: number
  closed: boolean
  min_stay: number
  source: 'manual' | 'channel'
}

// ── Search ────────────────────────────────────────────────────────────────────

export function usePropertySearch(params: ResolvedSearchParams) {
  return useQuery<PropertySearchResult[]>({
    queryKey: ['search', params],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('search_properties', {
        p_city:           params.city      || null,
        p_province:       params.province  || null,
        p_check_in:       params.check_in  || null,
        p_check_out:      params.check_out || null,
        p_adults:         params.adults,
        p_children:       params.children,
        p_rooms:          params.rooms,
        p_property_types: params.types.length ? params.types : null,
        p_min_price:      params.min_price  ?? null,
        p_max_price:      params.max_price  ?? null,
        p_amenities:      params.amenities.length ? params.amenities : null,
        p_limit:          params.limit,
        p_offset:         params.offset,
      })
      if (error) throw error
      return (data as PropertySearchResult[]) ?? []
    },
    staleTime: STALE.catalog,
  })
}

// ── Destination autocomplete ──────────────────────────────────────────────────

export interface DestinationSuggestion {
  city:           string
  province:       string
  property_count: number
  relevance:      number
}

/**
 * Suggestions for the search-bar destination dropdown. Empty query → trending
 * destinations (most properties first); non-empty → typeahead matches. Gate with
 * `enabled` so it only fires while the dropdown is open.
 */
export function useDestinationSuggestions(query: string, enabled = true) {
  const term = query.trim()
  return useQuery<DestinationSuggestion[]>({
    queryKey: ['destinations', term.toLowerCase()],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('suggest_destinations', {
        p_query: term || null,
        p_limit: term ? 8 : 6,
      })
      if (error) throw error
      return (data as DestinationSuggestion[]) ?? []
    },
    staleTime: STALE.catalog,
  })
}

// ── Property detail ───────────────────────────────────────────────────────────

export function useProperty(slug: string | undefined) {
  return useQuery<PropertyDetail>({
    queryKey: ['property', slug],
    enabled: !!slug,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('properties')
        .select(`
          id, slug, name, property_type, status, description,
          address_line1, city, province, zip, country,
          lat, lng, star_rating, check_in_time, check_out_time,
          vat_registered,
          promo_discount_pct, promo_starts_on, promo_ends_on,
          review_score, review_count,
          property_images ( id, cloudinary_public_id, display_order, room_type_id ),
          property_amenities ( amenity ),
          room_types (
            id, name, description, max_guests, base_occupancy, extra_guest_fee,
            free_child_min_age, free_child_max_age, paid_child_min_age, paid_child_max_age, child_fee,
            quantity, base_rate, size_sqm, bed_configuration,
            room_amenities ( amenity ),
            rate_plans (
              id, name, cancel_policy, free_cancel_deadline_days,
              cancel_penalty, meals_included, price_modifier_pct,
              discount_pct, discount_starts_on, discount_ends_on
            )
          )
        `)
        .eq('slug', slug!)
        .eq('status', 'live')
        .is('room_types.deleted_at', null)
        .is('room_types.rate_plans.deleted_at', null)
        .single()

      if (error) throw error

      // property_images is one flat list covering both the property (room_type_id
      // null) and individual rooms (room_type_id set). PostgREST can't split them
      // by relationship (no FK on room_type_id), so do it here:
      //   • main gallery → ALL photos, property-level first (so the hero/cover
      //     stays a property shot), then each room's photos.
      //   • each room's gallery → only that room's photos.
      const raw = data as Omit<PropertyDetail, 'property_images'> & {
        property_images: Array<PropertyImage & { room_type_id: string | null }>
      }
      const byOrder = (a: PropertyImage, b: PropertyImage) => a.display_order - b.display_order
      const roomPhotos = new Map<string, PropertyImage[]>()
      for (const img of raw.property_images) {
        if (img.room_type_id == null) continue
        const list = roomPhotos.get(img.room_type_id) ?? []
        list.push(img)
        roomPhotos.set(img.room_type_id, list)
      }

      const mainGallery = raw.property_images.slice().sort((a, b) => {
        const aRoom = a.room_type_id == null ? 0 : 1
        const bRoom = b.room_type_id == null ? 0 : 1
        if (aRoom !== bRoom) return aRoom - bRoom   // property-level photos first
        return a.display_order - b.display_order
      })

      return {
        ...raw,
        property_images: mainGallery,
        room_types: raw.room_types.map((rt) => ({
          ...rt,
          images: (roomPhotos.get(rt.id) ?? []).slice().sort(byOrder),
        })),
      } as PropertyDetail
    },
    staleTime: STALE.catalog,
  })
}

// ── Room availability ─────────────────────────────────────────────────────────

export function useRoomAvailability(
  propertyId: string | undefined,
  checkIn: string,
  checkOut: string,
) {
  return useQuery<RoomAvailabilityRow[]>({
    queryKey: ['availability', propertyId, { checkIn, checkOut }],
    enabled: !!propertyId && !!checkIn && !!checkOut && checkIn < checkOut,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_room_availability', {
        p_property_id: propertyId!,
        p_check_in:    checkIn,
        p_check_out:   checkOut,
      })
      if (error) throw error
      return (data as RoomAvailabilityRow[]) ?? []
    },
    staleTime: STALE.availability,
    refetchInterval: STALE.availability,
  })
}

// ── Reviews ───────────────────────────────────────────────────────────────────

export function usePropertyReviews(propertyId: string | undefined) {
  return useQuery<PropertyReview[]>({
    queryKey: ['reviews', propertyId],
    enabled: !!propertyId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('reviews')
        .select(`
          id, guest_id, score_overall,
          score_cleanliness, score_location, score_staff,
          score_value, score_comfort, score_facilities,
          comment, created_at,
          profiles ( full_name, avatar_url )
        `)
        .eq('property_id', propertyId!)
        .eq('published', true)
        .order('created_at', { ascending: false })
        .limit(20)

      if (error) throw error
      return (data as unknown as PropertyReview[]) ?? []
    },
    staleTime: STALE.catalog,
  })
}
